import type { CSSProperties, FC, ReactNode } from 'react'
import type { Material } from '@/constants/materials'

import { formatNumber } from '@/components/generals/count-up'
import { TweenNumber } from '@/components/generals/tween-number'
import { MaterialSample } from '@/components/materials/material-sample'
import { useInView } from '@/hooks/use-in-view'

import { MATERIALS, MAX_CARBON } from '@/constants/materials'

const delay = (value: number): CSSProperties => ({ '--delay': `${value}ms` } as CSSProperties)

interface MaterialRowProps {
    material: Material
    index: number
}

const MaterialRow: FC<MaterialRowProps> = ({ material, index }): ReactNode => {
    const [ref, inView] = useInView<HTMLLIElement>({ rootMargin: '0px 0px -15% 0px' })
    const ratio = material.carbon / MAX_CARBON

    return (
        <li
            ref={ref}
            id={material.id}
            data-reveal={inView ? 'in' : 'pre'}
            className='grid scroll-mt-20 gap-8 border-b border-hairline px-gutter py-12 desk:grid-cols-[4rem_minmax(0,1.1fr)_minmax(0,1.2fr)_minmax(0,0.7fr)] desk:gap-10 desk:py-16'
        >
            <span className='hud text-mute'>
                M.{String(index + 1).padStart(2, '0')}
            </span>

            <div>
                <h3 className='font-heading text-[clamp(30px,3.4vw,48px)] font-medium uppercase leading-none tracking-[-0.02em] text-ink'>
                    {material.name}
                </h3>

                <p className='hud mt-3 text-mute'>
                    {material.detail}
                </p>

                <p className='mt-8 font-heading text-[clamp(56px,6.4vw,96px)] font-medium leading-[0.9] tracking-[-0.04em] text-ink'>
                    <TweenNumber value={inView ? material.carbon : 0} duration={1400} />
                </p>

                <p className='hud mt-3 text-mute'>
                    kgCO2e per m³ / A1 to A3
                </p>

                <div className='mt-6 h-2 w-full bg-hairline/60' aria-hidden='true'>
                    <span className='draw-x block h-full bg-ink' style={{ width: `${Math.max(0.6, ratio * 100)}%`, ...delay(200) }} />
                </div>

                <p className='hud mt-2 flex justify-between text-mute' aria-hidden='true'>
                    <span>
                        0
                    </span>

                    <span>
                        {formatNumber(MAX_CARBON, 0)}
                    </span>
                </p>
            </div>

            <div>
                <p className='hud text-mute'>
                    Share of portfolio structural mass
                </p>

                <p className='mt-3 flex items-baseline gap-3'>
                    <span className='font-heading text-[40px] font-medium leading-none tracking-[-0.03em] text-ink'>
                        <TweenNumber value={inView ? material.share : 0} duration={1100} suffix='%' />
                    </span>

                    <span className='hud text-mute'>
                        {material.shareLabel}
                    </span>
                </p>

                <div className='mt-4 flex h-1.5 w-full gap-px' aria-hidden='true'>
                    {Array.from({ length: 50 }, (_, cell) => (
                        <span key={cell} className={cell < Math.round(material.share / 2) ? 'reveal-cell flex-1 bg-ink/80' : 'flex-1 bg-hairline/60'} style={delay(300 + cell * 12)} />
                    ))}
                </div>

                <p className='reveal-fade mt-8 max-w-[56ch] text-[15px] leading-[1.65] text-ink/80' style={delay(260)}>
                    {material.note}
                </p>
            </div>

            <div className='relative aspect-square w-full max-w-[280px] border border-hairline desk:justify-self-end'>
                <span className='hud absolute left-3 top-3 text-mute' aria-hidden='true'>
                    Sample / {material.name}
                </span>

                <MaterialSample kind={material.id} label={material.sampleLabel} className='reveal-cell absolute inset-4 top-8 h-[calc(100%-3rem)] w-[calc(100%-2rem)]' />
            </div>
        </li>
    )
}

/**
 * The five materials, each with its embodied carbon drawn as a bar on one shared scale.
 */
export const MaterialRows: FC = (): ReactNode => {
    return (
        <ol className='border-t border-hairline'>
            {MATERIALS.map((material, index) => (
                <MaterialRow key={material.id} material={material} index={index} />
            ))}
        </ol>
    )
}
